import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Gauge, LifeBuoy } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const shell = "flex min-h-screen items-center justify-center bg-slate-50 p-6";
const card = "panel w-full max-w-md p-5";
const input =
  "mt-1 h-9 w-full rounded-sm border border-slate-300 bg-white px-2.5 text-[13px] outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200";

const Brand = () => (
  <div className="flex items-center gap-2">
    <Gauge className="h-5 w-5 text-blue-600" strokeWidth={2.2} />
    <span className="font-display text-base font-bold tracking-tight text-slate-900">ImDash</span>
  </div>
);

export function AcceptInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/auth/invite/${token}`);
        setInfo(data);
      } catch (e) {
        setError(apiError(e));
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== repeat) {
      setError("De wachtwoorden zijn niet gelijk");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/accept-invite", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      setError(apiError(err));
    }
    setBusy(false);
  };

  return (
    <div className={shell}>
      <form onSubmit={submit} className={card} data-testid="accept-invite-form">
        <Brand />
        <h1 className="mt-4 font-display text-lg font-semibold text-slate-900">
          Welkom bij ImDash
        </h1>
        {info && (
          <p className="mt-1 text-[12px] text-slate-500" data-testid="invite-info">
            Stel je wachtwoord in voor <strong>{info.email}</strong>
            {info.company ? ` (${info.company})` : ""}.
          </p>
        )}
        {done ? (
          <div
            className="mt-4 rounded-sm border border-emerald-300 bg-emerald-50 px-2.5 py-2 text-[12px] text-emerald-800"
            data-testid="invite-success"
          >
            Je wachtwoord is ingesteld. Je wordt naar de inlogpagina gestuurd.
          </div>
        ) : (
          <>
            <label className="label-xs mt-4 block">Wachtwoord (min. 8 tekens)</label>
            <input
              type="password"
              minLength={8}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="invite-password-input"
              className={input}
            />
            <label className="label-xs mt-3 block">Wachtwoord herhalen</label>
            <input
              type="password"
              minLength={8}
              required
              value={repeat}
              onChange={(e) => setRepeat(e.target.value)}
              data-testid="invite-password-repeat"
              className={input}
            />
            {error && (
              <div
                className="mt-3 rounded-sm border border-red-300 bg-red-50 px-2.5 py-2 text-[12px] text-red-800"
                data-testid="invite-error"
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={busy || !info}
              className="btn-primary mt-4 w-full"
              data-testid="invite-submit"
            >
              {busy ? "Opslaan…" : "Wachtwoord instellen"}
            </button>
          </>
        )}
        <Link to="/login" className="mt-3 block text-center text-[12px] text-blue-700">
          Naar inloggen
        </Link>
      </form>
    </div>
  );
}

export function AccountPaused() {
  const { accessReason, account, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className={shell}>
      <div className={card} data-testid="account-paused">
        <Brand />
        <div className="mt-4 flex items-start gap-2">
          <LifeBuoy className="mt-0.5 h-5 w-5 text-amber-600" />
          <div>
            <h1 className="font-display text-lg font-semibold text-slate-900">
              ImDash account tijdelijk gepauzeerd
            </h1>
            <p className="mt-1 text-[13px] text-slate-600">
              {accessReason || "Je account is momenteel niet actief."} Neem contact op met ImDash om
              je account opnieuw te activeren.
            </p>
            {account?.name && (
              <p className="mt-2 text-[12px] text-slate-500">Account: {account.name}</p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={async () => {
            await logout();
            navigate("/login");
          }}
          className="btn-secondary mt-5 w-full"
          data-testid="paused-logout"
        >
          Uitloggen
        </button>
      </div>
    </div>
  );
}
